import { dehydrate, hydrate, type DehydratedState, type QueryClient } from "@tanstack/react-query"
import { depoOku, depoSil, depoYaz } from "./depo"
import { fetchContentType } from "@/lib/content-types"
import { fetchChecklist } from "@/lib/checklists"
import { checklistKeys } from "@/hooks/use-checklists"
import { fetchAuditSetContent } from "@/lib/audit-sets"
import { fetchProjectSessions, fetchSessionDetail } from "@/lib/checklist-entries"
import {
  fetchDosyaFormlari, fetchElevatorProject, fetchEslesenKontrolListeleri, fetchGeneralForm,
} from "@/lib/elevator-projects"

/**
 * ÇEVRİMDIŞI OKUMA — sorgu önbelleği cihazda (IndexedDB) saklanır.
 *
 * Başarılı her sorgunun sonucu kısa gecikmeyle diske yazılır; uygulama
 * açılırken geri yüklenir. Bağlantı yokken sorgular sunucuya gidemez ama
 * ekranlar son bilinen veriyle açılır. "Çevrimdışına al" (`dosyayiIndir`)
 * sahaya çıkmadan önce bir dosyanın bütün ekranlarının verisini baştan çeker
 * — yalnız gezilen ekranların değil.
 *
 * 7 günden eski kayıtlar yüklenmez: eski veriyle doldurulan form sunucudaki
 * yeniyi ezmesin.
 */

const DEPO_ANAHTARI = "sorgular"
const EN_FAZLA_YAS_MS = 7 * 24 * 60 * 60 * 1000
/** Bellekte tutma süresi — gözlemcisi olmayan sorgu da çevrimdışında lazım olabilir. */
export const CEVRIMDISI_GC_MS = 24 * 60 * 60 * 1000

/** Cihazda saklanmayan sorgular: anlık sayaçlar, imzalı (kısa ömürlü) adresler. */
const SAKLANMAZ = ["notifications", "unread", "elevator-project-form-file-url", "file-url", "presigned"]
const saklanirMi = (anahtar: readonly unknown[]) =>
  !anahtar.some((p) => typeof p === "string" && SAKLANMAZ.some((s) => p.includes(s)))

/** Açılışta: diskteki önbelleği yükler (çok sürerse beklemeden devam). */
export async function onbellegiYukle(qc: QueryClient, zamanAsimiMs = 1500) {
  const oku = depoOku<{ zaman: number; durum: DehydratedState }>(DEPO_ANAHTARI)
  const kayit = await Promise.race([oku, new Promise<undefined>((r) => setTimeout(r, zamanAsimiMs))])
  if (!kayit?.durum) return
  const sinir = Date.now() - EN_FAZLA_YAS_MS
  hydrate(qc, {
    ...kayit.durum,
    queries: kayit.durum.queries
      .filter((q) => q.state.dataUpdatedAt >= sinir)
      // Son çekmesi düşmüş (çevrimdışı) sorgu da elindeki veriyle başarılı açılsın.
      .map((q) => ({ ...q, state: { ...q.state, status: "success" as const, error: null, fetchStatus: "idle" as const } })),
  })
}

/** Önbellek değiştikçe (2 sn gecikmeyle, toplu) diske yazar. */
export function onbellegiSakla(qc: QueryClient) {
  let zamanlayici: number | null = null
  const yaz = () => {
    zamanlayici = null
    const durum = dehydrate(qc, {
      /* Verisi olan her sorgu: bağlantı yokken yeniden çekme düşer ve sorgu
         "hata" durumuna geçer, ama elindeki veri hâlâ geçerli — atlanırsa
         bir sonraki kayıtta dosyanın verisi diskten silinirdi. */
      shouldDehydrateQuery: (q) => q.state.data !== undefined && saklanirMi(q.queryKey),
      shouldDehydrateMutation: () => false,
    })
    void depoYaz(DEPO_ANAHTARI, { zaman: Date.now(), durum })
  }
  return qc.getQueryCache().subscribe((olay) => {
    if (olay.type !== "updated" && olay.type !== "removed") return
    if (zamanlayici) window.clearTimeout(zamanlayici)
    zamanlayici = window.setTimeout(yaz, 2000)
  })
}

/** Çıkışta: cihazdaki kopya silinir (başka kullanıcı görmesin). */
export function cevrimdisiOnbellegiSil() {
  return depoSil(DEPO_ANAHTARI)
}

/**
 * DOSYAYI ÇEVRİMDIŞINA AL — dosyanın bütün doldurma ekranlarının verisini
 * önbelleğe çeker: künye, denetim seti, formlar ve şemaları, kontrol
 * listeleri ve oturumları. Anahtarlar ekranların kendi sorgularıyla AYNI
 * olmalı; aksi hâlde çekilen veri çevrimdışında kimse tarafından okunmaz.
 *
 * Dönen sayı: çekilen kaynak sayısı (kullanıcıya bilgi).
 */
export async function dosyayiIndir(qc: QueryClient, projectId: string): Promise<number> {
  const cek = <T,>(queryKey: readonly unknown[], queryFn: () => Promise<T>) =>
    qc.fetchQuery({ queryKey, queryFn, staleTime: 0 })
  let sayi = 0
  const say = <T,>(p: Promise<T>) => p.then((v) => { sayi++; return v })

  const proje = await say(cek(["elevator-project", projectId], () => fetchElevatorProject(projectId)))
  const setId = proje.inspectionSets?.[0]?.auditSetId ?? null
  const [icerik, oturumlar, listeler] = await Promise.all([
    setId ? say(cek(["audit-set-content", setId], () => fetchAuditSetContent(setId))) : Promise.resolve(null),
    say(cek(["elevator-project-sessions", projectId], () => fetchProjectSessions(projectId))),
    say(cek(["elevator-project-checklists", projectId], () => fetchEslesenKontrolListeleri(projectId))),
    say(cek(["elevator-project-forms", projectId], () => fetchDosyaFormlari(projectId))),
    say(cek(["elevator-project-general-form", projectId, proje.scopeId ?? null, setId], () => fetchGeneralForm(projectId))),
  ])

  const formAnahtarlari = new Set(
    (icerik?.items ?? []).filter((i) => i.kind === "CONTENT_TYPE" && i.contentTypeKey).map((i) => i.contentTypeKey!),
  )
  const listeIdleri = new Set([
    ...(listeler?.listeler ?? []).map((l) => l.checklistId),
    ...Object.values(listeler?.guncel ?? {}),
  ])
  await Promise.allSettled([
    ...[...formAnahtarlari].map((k) => say(cek(["content-type", k], () => fetchContentType(k)))),
    ...[...listeIdleri].map((id) => say(cek(checklistKeys.detail(id), () => fetchChecklist(id)))),
    ...(oturumlar?.data ?? []).map((s) =>
      say(cek(["session-detail", projectId, s.id], () => fetchSessionDetail(projectId, s.id)))),
  ])
  return sayi
}
